import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const INIT = { name: '', email: '', password: '', role: 'staff' };
const LIMIT = 15;

export default function Users({ setTitle }) {
  const [items, setItems]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [form, setForm]       = useState(INIT);
  const [editId, setEditId]   = useState(null);
  const [saving, setSaving]   = useState(false);

  useEffect(() => { setTitle('Users'); }, [setTitle]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/auth/users?search=${encodeURIComponent(search)}&page=${page}&limit=${LIMIT}`);
      // backend may return array or {data, total}
      if (Array.isArray(data)) { setItems(data); setTotal(data.length); }
      else { setItems(data.data || []); setTotal(data.total || 0); }
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [search, page]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => { setForm({ name: item.name, email: item.email, role: item.role, password: '' }); setEditId(item.id); setModal(true); };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) return alert('Name is required');
    if (!form.email.trim()) return alert('Email is required');
    if (!editId && !form.password) return alert('Password is required for new users');
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.password) payload.password = form.password;
      if (editId) await api.put(`/auth/users/${editId}`, payload);
      else        await api.post('/auth/users', { ...payload, password: form.password });
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving user');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    try { await api.delete(`/auth/users/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting user'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  const roleColors = { admin: 'badge-admin', manager: 'badge-manager', staff: 'badge-staff' };

  return (
    <div className="page">
      <div className="page-header">
        <h1>Users</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add User
        </button>
      </div>

      <div className="card">
        <div className="toolbar">
          <div className="search-box">
            <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input placeholder="Search by name or email…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} users</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="empty">No users found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td><strong>{item.name}</strong></td>
                    <td className="text-muted">{item.email}</td>
                    <td><span className={`badge ${roleColors[item.role] || 'badge-staff'}`}>{item.role}</span></td>
                    <td className="text-muted">{item.created_at ? item.created_at.slice(0, 10) : '—'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id, item.name)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit User' : 'Add User'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group full">
                  <label>Full Name *</label>
                  <input className="form-control" value={form.name} onChange={e => f('name', e.target.value)} placeholder="User full name" />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" className="form-control" value={form.email} onChange={e => f('email', e.target.value)} placeholder="user@company.com" />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select className="form-control" value={form.role} onChange={e => f('role', e.target.value)}>
                    <option value="admin">Admin</option>
                    <option value="manager">Manager</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>
                <div className="form-group full">
                  <label>{editId ? 'New Password (leave blank to keep current)' : 'Password *'}</label>
                  <input type="password" className="form-control" value={form.password} onChange={e => f('password', e.target.value)} placeholder={editId ? 'Leave blank to keep current password' : 'Minimum 6 characters'} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update User' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
